"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MenuContainerContext = exports.MenuContainer = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _web = require("@react-hookz/web");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _useTheme = require("../../../theme/utils/useTheme");
var _blanket = require("../../blanket");
var _focusMode = require("../../focusMode");
var _useFocusEventListener = require("../../focusMode/hooks/useFocusEventListener");
var _MenuParentContext = require("../context/MenuParentContext");
var _FocusDelegate = require("../hooks/FocusDelegate");
var _MenuContainerStyled = require("../styled/MenuContainerStyled");
// when the user presses the up arrow while the target is focused.

const elementIsScrollable = elem => !!elem && elem.offsetHeight < elem.scrollHeight;
// Tanner: Well, I don't really like this but without being able to pass arbitrary props
// to the Flyout's FlyoutContainer override, we can't get props into the MenuContainer
// any other way.
// TODO We should follow-up with this once the overrides system in Flyout is augmented to
// TODO allow passing a Component AND extra props to it. Tanner did a BUNCH of ts work to
// TODO make doing that type-safe and didn't get it to work. Other component libs that have
// TODO overrides just use any everywhere to get around it. We'll probably have to do the same.
const defaultValue = {
  applyMinWidth: false,
  clientId: undefined,
  parent: false,
  placement: "bottom-start",
  loop: true,
  focusStrategy: "none",
  onCloseRequested: () => undefined,
  includeBlanket: false
};
const MenuContainerContext = (0, _lodestarUtils.createContext)(defaultValue, "MenuContainerContext");
exports.MenuContainerContext = MenuContainerContext;
const keyCodes = new Set([]);
const keyEvents = new Set([]);
const mouseEvents = new Set(["mousemove"]);
const MenuContainer = /*#__PURE__*/(0, _react.forwardRef)((_ref, parentRef) => {
  let {
    children,
    onKeyDown,
    onMouseLeave,
    ...props
  } = _ref;
  const {
    applyMinWidth,
    clientId,
    placement,
    loop,
    focusStrategy,
    onCloseRequested,
    parent,
    includeBlanket
  } = (0, _react.useContext)(MenuContainerContext);
  const {
    closeMenuRootRequested,
    layout
  } = (0, _MenuParentContext.useMenuParentContext)();
  const [dir] = placement.split("-");

  // Used to manage focus interactions inside the Menu.
  const {
    current: focusDelegate
  } = (0, _react.useRef)(new _FocusDelegate.FocusDelegate());
  const callbackRef = (0, _react.useCallback)(newElement => {
    if (newElement) {
      focusDelegate.setFocusables(newElement);
    }
  }, [focusDelegate]);
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  (0, _react.useLayoutEffect)(() => {
    // This can be set on the initial render, or it can initially be "none"
    // and then change to first/last if the mouse clicked the target and
    // then pressed up/down to select a menuitem.
    switch (focusStrategy) {
      case "first":
        focusDelegate.getFirstElement()?.focus();
        break;
      case "last":
        focusDelegate.getLastElement()?.focus();
        break;
    }
  }, [focusDelegate, focusStrategy]);
  const localRef = (0, _react.useRef)(null);
  const ref = (0, _lodestarUtils.useForkRef)(parentRef, localRef, callbackRef);

  // If we add or remove a scrollbar, we'll re-render. Normally we'd have to
  // add a window onresize listener as well, but popper will already re-render
  // when a resize occurs.
  const isScrollable = elementIsScrollable(localRef.current);
  const previousIsScrollable = (0, _web.usePrevious)(isScrollable);
  const rerender = (0, _web.useRerender)();
  (0, _react.useLayoutEffect)(() => {
    if (previousIsScrollable !== isScrollable) {
      rerender();
    }
  }, [isScrollable, previousIsScrollable, rerender]);

  // In React <=16 handlers are all attached at the doc-level, meaning our local handlers
  // to this component will be called well after the time when they are able to prevent
  // the browser's standard key handling for keys that can cause scrolling.
  // Because we want these keys to do something in the Menu, we need to block them
  // from propagating to the document (window) level handlers.
  (0, _react.useEffect)(() => {
    const disableWindowKeyHandling = event => {
      switch (event.key) {
        // TODO we probably need to handle PageUp/PageDown here too, but we can add that later.
        case "Home":
        case "End":
          event.stopPropagation();
          event.preventDefault();
          break;
      }
    };
    window.addEventListener("keydown", disableWindowKeyHandling);
    return () => {
      window.removeEventListener("keydown", disableWindowKeyHandling);
    };
  }, []);

  // Handles key navigation between menuitems in the menu.
  const localOnKeyDown = (0, _react.useCallback)(event => {
    if (event.defaultPrevented) {
      return;
    }
    const activeElement = document.activeElement;
    if (!activeElement) {
      return;
    }
    switch (event.key) {
      case "Escape":
        onCloseRequested();
        // Dont' stop propagation here, each menu will close itself in reverse.
        break;
      case "Tab":
        // This will definitely be defined, it's just the typings that say it can be
        // nullable since the top-level context CAN actually be nullable.
        closeMenuRootRequested?.();
        // We need to prevent this or the focus will move along to the start of the DOM
        // again. We portal this component out to the end of the body.
        event.preventDefault();
        break;
      case "ArrowDown":
        {
          const below = focusDelegate.getElementBelow(activeElement);
          if (below) {
            below.focus();
          } else if (loop) {
            // Loop around to the top.
            focusDelegate.getFirstElement()?.focus();
          }
          event.stopPropagation();
          event.preventDefault();
        }
        break;
      case "ArrowUp":
        {
          const above = focusDelegate.getElementAbove(activeElement);
          if (above) {
            above.focus();
          } else if (loop) {
            // Loop around to the bottom.
            focusDelegate.getLastElement()?.focus();
          }
          event.stopPropagation();
          event.preventDefault();
        }
        break;
      case "ArrowRight":
        // If submenu is in that direction, open it.
        // If submenu is in opposite direction, close it.
        if (dir === "left") {
          onCloseRequested();
          event.stopPropagation();
        }
        break;
      case "ArrowLeft":
        // If submenu is in that direction, open it.
        // If submenu is in opposite direction, close it.
        if (dir === "right") {
          onCloseRequested();
          event.stopPropagation();
        }
        break;
      case "Home":
        focusDelegate.getFirstElement()?.focus();
        event.stopPropagation();
        break;
      case "End":
        focusDelegate.getLastElement()?.focus();
        event.stopPropagation();
        break;
    }
  }, [onCloseRequested, closeMenuRootRequested, dir, focusDelegate, loop]);
  const combinedOnKeyDown = (0, _lodestarUtils.callAllHandlers)(onKeyDown, localOnKeyDown);

  // When the mouse leaves a menu, blur the currently focused item.
  // Managing focus like this might seem weird, but the focus mode isn't changed on mousemove
  // as of lodestar-core v1.69.0. When we force focus out of the menu in this component, we
  // want to set focusMode back to mouse so things are sync'd properly. Focus goes to body when
  // you blur the active element. See the onMouseLeave handler below.
  // Another reason we do this is to allow the onMouseEnter handler in MenuItemBase
  // to properly handle the focus change. Without resetting this, the hovered
  // menuitem will not receive focus.
  // In this component, we don't listen to any keyboard events, but we do listen to mousemove.
  // This means that when the mouse moves, focusMode goes back to mouse. This breaks away from
  // the usual :focus-visible heuristic but is more closely aligned to how OS menus work.
  const {
    focusMode,
    setFocusMode
  } = (0, _focusMode.useFocusMode)();
  (0, _useFocusEventListener.useFocusEventListener)({
    focusMode,
    setFocusMode,
    keyCodes,
    mouseEvents,
    keyEvents
  });
  const localOnMouseLeave = (0, _react.useCallback)(event => {
    if (event.defaultPrevented) {
      return;
    }
    if (document.activeElement && (0, _lodestarUtils.contains)(localRef.current, document.activeElement)) {
      document.activeElement.blur();
      event.stopPropagation();
    }
  }, []);
  const combinedOnMouseLeave = (0, _lodestarUtils.callAllHandlers)(onMouseLeave, localOnMouseLeave);
  const combinedOnClick = (0, _lodestarUtils.callAllHandlers)(onCloseRequested, e => {
    e.stopPropagation();
  });
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, includeBlanket &&
  // Only the root menu gets a blanket, everything else will reuse the root's blanket
  !parent && /*#__PURE__*/_react.default.createElement(_blanket.Blanket, {
    onClick: combinedOnClick
    // Since this whole component is within a stacking context, setting this to 0
    // means the blanket will always be under the menu since it's rendered first.
    ,
    zIndex: 0,
    clientId: (0, _lodestarUtils.getClientId)(clientId, "blanket")
  }), /*#__PURE__*/_react.default.createElement(_MenuContainerStyled.MenuContainerStyled, (0, _extends2.default)({}, props, {
    applyMinWidth: applyMinWidth,
    onKeyDown: combinedOnKeyDown,
    onMouseLeave: combinedOnMouseLeave,
    ref: ref,
    themeKey: themeMode,
    placement: placement,
    layout: layout
  }), children));
});
exports.MenuContainer = MenuContainer;
MenuContainer.displayName = "MenuContainer";
//# sourceMappingURL=MenuContainer.js.map